import React, { useContext } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  Drawer,
  IconButton,
  useMediaQuery,
  Divider,
  Tooltip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import { ThemeContext } from "../../utils/ThemeContext";

const SIDEBAR_W = 260;

const MemberSidebar = ({ userFullName, handleSignOut, accounts = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { theme } = useContext(ThemeContext);

  const isDark = theme.mode === "dark";
  const sidebarBg = theme.sidebarColor || (isDark ? "#0d1b2a" : "#26323d");
  const gradientEnd = isDark ? "#060e18" : "#202b35";
  const sidebarBorder = "rgba(255,255,255,0.08)";
  const hoverBg = "rgba(255,255,255,0.08)";
  const activeBg = "rgba(255,255,255,0.16)";
  const textColor = "#f4f7fb";
  const mutedText = "rgba(255,255,255,0.6)";

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const activeAccount = searchParams.get("account");

  const handleAccountSwitch = (accId) => {
    navigate(`/home/memberspage?account=${accId}`);
    if (isMobile) setDrawerOpen(false);
  };

  const initials = userFullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  const onSignOut = async () => {
    if (isMobile) setDrawerOpen(false);
    if (typeof handleSignOut === "function") await handleSignOut();
  };

  const navItems = [
    {
      label: "My Dashboard",
      icon: <DashboardIcon />,
      path: "/home/memberspage",
    },
    {
      label: "My Profile",
      icon: <PersonOutlineIcon />,
      path: "/home/user-profile",
    },
  ];

  const navBtn = (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    minHeight: "48px",
    padding: "10px 16px",
    background: active ? activeBg : "transparent",
    border: "none",
    borderRadius: "12px",
    color: textColor,
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: active ? 700 : 500,
    transition: "background 0.18s ease",
    marginBottom: "4px",
    textAlign: "left",
  });

  const SidebarContent = (
    <Box
      sx={{
        width: `${SIDEBAR_W}px`,
        background: `linear-gradient(180deg, ${sidebarBg} 0%, ${gradientEnd} 100%)`,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        color: textColor,
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 1200,
        borderRight: `1px solid ${sidebarBorder}`,
        boxShadow: "8px 0 30px rgba(0,0,0,0.18)",
      }}
    >
      {/* Logo */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 3, pb: 2 }}>
        <img
          src="/img/logo2.png"
          alt="MY HOA"
          style={{ width: "108px", height: "auto", objectFit: "contain",
            filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.22))" }}
        />
      </Box>

      <Divider sx={{ borderColor: sidebarBorder, mx: 2 }} />

      {/* Member identity card */}
      <Box
        sx={{
          mx: 2,
          mt: 2.5,
          mb: 2,
          p: 2,
          borderRadius: "14px",
          backgroundColor: "rgba(255,255,255,0.07)",
          border: `1px solid ${sidebarBorder}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            width: 42,
            height: 42,
            backgroundColor: "rgba(47,99,173,0.7)",
            color: "#fff",
            fontWeight: 700,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          {initials || <HomeWorkIcon sx={{ fontSize: 22 }} />}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: textColor, lineHeight: 1.3,
              wordBreak: "break-word" }}
          >
            {userFullName || "Member"}
          </Typography>
          <Typography variant="caption" sx={{ color: mutedText }}>
            Homeowner
          </Typography>
        </Box>
      </Box>

      {/* Nav items */}
      <Box sx={{ flex: 1, px: 1.5, overflowY: "auto" }}>
        <Typography
          variant="caption"
          sx={{ color: mutedText, fontWeight: 600, px: 1.5, mb: 1,
            display: "block", letterSpacing: "0.08em", textTransform: "uppercase",
            fontSize: "0.7rem" }}
        >
          Navigation
        </Typography>

        {navItems.map(({ label, icon, path }) => {
          const active = isActive(path);
          return (
            <Tooltip key={path} title="" placement="right">
              <button
                onClick={() => handleNav(path)}
                style={navBtn(active)}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center",
                  color: active ? "#7fb3ff" : textColor, flexShrink: 0 }}>
                  {icon}
                </Box>
                <span>{label}</span>
                {active && (
                  <Box
                    sx={{
                      ml: "auto",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#7fb3ff",
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            </Tooltip>
          );
        })}
      </Box>

      {/* Account switcher. only shown when member has multiple accounts */}
      {accounts.length > 1 && (
        <Box
          sx={{
            px: 1.5,
            pb: 1.5,
            borderTop: `1px solid ${sidebarBorder}`,
            pt: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: mutedText,
              fontWeight: 600,
              px: 1,
              mb: 1,
              display: "block",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontSize: "0.7rem",
            }}
          >
            My Accounts
          </Typography>
          {accounts.map((accId) => {
            const isSelected = activeAccount === accId;
            return (
              <button
                key={accId}
                onClick={() => handleAccountSwitch(accId)}
                style={{
                  ...navBtn(isSelected),
                  justifyContent: "flex-start",
                  gap: "10px",
                  paddingLeft: "12px",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = hoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "transparent";
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    backgroundColor: isSelected
                      ? "rgba(127,179,255,0.22)"
                      : "rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <HomeWorkIcon
                    sx={{
                      fontSize: 15,
                      color: isSelected ? "#7fb3ff" : mutedText,
                    }}
                  />
                </Box>
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: isSelected ? "#7fb3ff" : textColor,
                    fontWeight: isSelected ? 700 : 500,
                  }}
                >
                  Account #{accId}
                </span>
                {isSelected && (
                  <Box
                    sx={{
                      ml: "auto",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "#7fb3ff",
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })}
        </Box>
      )}

      {/* Footer. Sign Out */}
      <Box
        sx={{
          px: 1.5,
          pb: 2,
          pt: 1,
          borderTop: `1px solid ${sidebarBorder}`,
          backgroundColor: "rgba(0,0,0,0.1)",
        }}
      >
        <button
          onClick={onSignOut}
          style={{
            ...navBtn(false),
            background: "rgba(255,255,255,0.05)",
            border: `1px solid ${sidebarBorder}`,
            justifyContent: "flex-start",
            marginBottom: "8px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255,80,80,0.14)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }}
        >
          <LogoutIcon sx={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 600 }}>Sign Out</span>
        </button>

        <Typography
          variant="caption"
          sx={{ color: mutedText, display: "block", textAlign: "center",
            fontSize: "0.75rem", mt: 0.5 }}
        >
          © My HOA {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              position: "fixed", top: 12, left: 12, zIndex: 1300,
              color: "#fff", backgroundColor: sidebarBg,
              border: `1px solid ${sidebarBorder}`,
              boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
              "&:hover": { backgroundColor: "#2d3a46" },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: `${SIDEBAR_W}px`,
                background: `linear-gradient(180deg, ${sidebarBg} 0%, ${gradientEnd} 100%)`,
                color: textColor,
                borderRight: `1px solid ${sidebarBorder}`,
              },
            }}
          >
            {SidebarContent}
          </Drawer>
        </>
      ) : (
        SidebarContent
      )}
    </>
  );
};

export { SIDEBAR_W };
export default MemberSidebar;
